/**
 * IXVerify Notification Helpers
 * Creates/updates notifications via the platform IXVerify API
 * (POST/PATCH /api/ixverify/notifications*) instead of a direct supabase client.
 */

import {
  createNotifications,
  setNotificationRead,
  markAllNotificationsReadApi,
  getUnreadNotificationCount,
  notifyAdminsMismatchApi,
} from './ixverifyApi';

export type IXVerifyNotificationType =
  | 'verification_complete'
  | 'verification_failed'
  | 'new_user'
  | 'system'
  | 'shipment_complete';

interface CreateNotificationParams {
  userId: string;
  type: IXVerifyNotificationType;
  title: string;
  message?: string;
  verificationId?: string;
  shipmentId?: string;
}

/**
 * Create a notification for a specific user
 */
export async function createNotification({
  userId,
  type,
  title,
  message,
  verificationId,
  shipmentId,
}: CreateNotificationParams): Promise<void> {
  try {
    const { error } = await createNotifications({
      user_id: userId,
      type,
      title,
      message: message || null,
      verification_id: verificationId || null,
      shipment_id: shipmentId || null,
      read: false,
    });

    if (error) {
      console.error('Error creating notification:', error);
    }
  } catch (error) {
    console.error('Error creating notification:', error);
  }
}

/**
 * Create verification completion notification for the user who performed it
 */
export async function notifyVerificationComplete(
  userId: string,
  verificationId: string,
  ixrsNumber: string,
  matchResult: 'match' | 'mismatch'
): Promise<void> {
  const isMatch = matchResult === 'match';
  await createNotification({
    userId,
    type: isMatch ? 'verification_complete' : 'verification_failed',
    title: isMatch
      ? `Verification Complete - Match`
      : `Verification Alert - Mismatch`,
    message: `IXRS ${ixrsNumber} verification ${isMatch ? 'successful' : 'failed'}`,
    verificationId,
  });
}

/**
 * Notify admins at a site about a mismatch
 */
export async function notifyAdminsOfMismatch(
  siteId: string,
  performedByEmail: string,
  verificationId: string,
  ixrsNumber: string,
  vialNumber: string
): Promise<void> {
  try {
    // Server-side: looks up IXVerify site admins (tool_user_roles), excludes the
    // performer, and inserts one 'verification_failed' notification per admin.
    const { error } = await notifyAdminsMismatchApi({
      siteId,
      performedByEmail,
      verificationId,
      ixrsNumber,
      vialNumber,
    });
    if (error) {
      console.error('Error notifying admins:', error);
    }
  } catch (error) {
    console.error('Error notifying admins:', error);
  }
}

/**
 * Mark a notification as read
 */
export async function markNotificationRead(notificationId: string): Promise<void> {
  try {
    const { error } = await setNotificationRead(notificationId, true);

    if (error) {
      console.error('Error marking notification as read:', error);
    }
  } catch (error) {
    console.error('Error marking notification as read:', error);
  }
}

/**
 * Mark all notifications as read for a user
 */
export async function markAllNotificationsRead(_userId: string): Promise<void> {
  try {
    // Server marks all of the authenticated caller's unread notifications read.
    const { error } = await markAllNotificationsReadApi();

    if (error) {
      console.error('Error marking all notifications as read:', error);
    }
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
  }
}

/**
 * Get unread notification count for a user
 */
export async function getUnreadCount(_userId: string): Promise<number> {
  try {
    // Server counts the authenticated caller's unread notifications.
    const { data, error } = await getUnreadNotificationCount();

    if (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }

    return data || 0;
  } catch (error) {
    console.error('Error getting unread count:', error);
    return 0;
  }
}
